































import { Prop, Component, Vue } from "vue-property-decorator";
import _ from "lodash";
import ChartRow from "@/models/chart-row";
import parseDate from "@/helpers/parseDate";

import { areIntervalsOverlapping, endOfDay, addDays } from "date-fns";
import ChartBar from "@/models/chart-bar";
import ChartBarFactory from "@/models/factories/chart-bar-factory";
import Chart from "@/models/chart";
import ProjectTemplate from "../models/project-template";
import { templateApi } from "../api";
import ProjectTemplateTask from "../models/project-template-task";
import ProjectTemplateTaskGroup from "../models/project-template-task-group";
import { TaskType, getTaskTypes } from "../api/task-type-api";
import SelectOption from "@/common/ui/presenters/select-option";

import PlanEdit from "@/templates/PlanEdit.vue";
import PlanTaskEditModal from "../components/PlanTaskEditModal.vue";
import PlanTaskGroupEditModal from "../components/PlanTaskGroupEditModal.vue";
import endOfDuration from "../helpers/endOfDuration";

@Component({
  components: {
    PlanEdit,
    PlanTaskEditModal,
    PlanTaskGroupEditModal
  }
})
export default class PlanEditPage extends Vue {
  @Prop({ required: true }) id!: string;
  baseSpan = 35;
  baseDate = parseDate("2000-01-01");
  chart = new Chart(this.baseDate, 10);
  start = 0;
  template: ProjectTemplate = new ProjectTemplate();
  taskTypes: TaskType[] = [];

  get cols(): { text: string; color: string }[] {
    const start = this.start + 1;
    const stop = start + this.chart.cols;
    return _.range(start, stop).map(i => ({
      text: String(i),
      color: i % 5 ? "" : "var(--mask)"
    }));
  }

  get rows(): ChartRow[] {
    return this.template.taskGroups.map(g => ({
      id: g.id,
      label: this.getTaskTypeName(g.taskTypeId),
      actions: [],
      tasks: [],
      bars: g.tasks.filter(this.includesTask).map(this.createTaskBar)
    }));
  }

  get taskTypeOptions(): SelectOption[] {
    return this.taskTypes.map(t => ({
      text: t.name,
      value: t.taskTypeId
    }));
  }

  async newTaskGroup() {
    const group = {
      id: 0,
      tasks: [],
      taskTypeId: 0,
      projectTemplateId: this.template.id
    };
    await templateApi.createTaskGroup(group);
    await this.initTemplate();
  }

  async sort(rows: ChartRow[]) {
    await templateApi.sortTaskGroups(this.template.id, rows.map(r => r.id));
    await this.initTemplate();
  }

  editTaskGroup(id: number) {
    const group = this.taskGroups.find(g => g.id === id);
    if (!group) return;
    group.projectTemplateId = Number(this.template.id);
    (this.$refs.editGroup as PlanTaskGroupEditModal).show({ ...group });
  }

  async saveTaskGroup(group: ProjectTemplateTaskGroup) {
    await this.updateTaskGroup(group);
    this.$nextTick(() => {
      (this.$refs.editGroup as PlanTaskGroupEditModal).hide();
    });
  }

  async deleteTaskGroup(group: ProjectTemplateTaskGroup) {
    await templateApi.deleteTaskGroup(group.id);
    await this.initTemplate();
  }

  newTask(groupId: number, colIndex: number) {
    const start = addDays(this.chart.start, colIndex);
    const task = {
      id: 0,
      name: "",
      start,
      end: endOfDay(start),
      duration: 1,
      taskGroupId: groupId
    };
    (this.$refs.editTask as PlanTaskEditModal).show(task);
  }

  editTask(id: number) {
    const task = this.tasks.find(t => t.id === id);
    if (!task) return;
    (this.$refs.editTask as PlanTaskEditModal).show({ ...task });
  }

  saveTask(task: ProjectTemplateTask) {
    if (task.id) {
      this.updateTask(task);
    } else {
      this.createTask(task);
    }
    this.$nextTick(() => {
      // TODO すぐに閉じようとすると閉じない。条件は？
      (this.$refs.editTask as PlanTaskEditModal).hide();
    });
  }

  async deleteTask(task: ProjectTemplateTask) {
    if (!confirm("本当に削除しますか？")) return;
    await templateApi.deleteTask(task.id);
    await this.initTemplate();
  }

  private task?: ProjectTemplateTask;

  change(bar: ChartBar) {
    this.task = this.tasks.find(t => t.id === bar.id);
    if (!this.task) return;
    if (bar.start < 0) return; // TODO 基準値ベースにする
    const start = addDays(this.chart.start, bar.start);
    const end = endOfDuration(start, bar.span);
    Object.assign(this.task, { start, end, duration: bar.span });
  }

  submit() {
    if (this.task) {
      this.updateTask(this.task);
    }
  }

  cancel() {
    this.initTemplate();
  }

  async saveTemplate(template: ProjectTemplate) {
    await templateApi.save(template);
    this.initTemplate();
  }

  goPrev() {
    if (this.start < 10) {
      this.chart.move(-this.start);
      this.start = 0;
    } else {
      this.chart.move(-10);
      this.start -= 10;
    }
  }

  goNext() {
    this.chart.move(10);
    this.start += 10;
  }

  async remove() {
    if (!confirm("本当に削除しますか？")) return;
    await templateApi.destroy(this.template.id);
    this.$router.push({ name: "plan-list" });
  }

  resize(width: number) {
    this.chart.adjust(width);
  }

  private get tasks(): ProjectTemplateTask[] {
    return this.template.taskGroups.flatMap(g => g.tasks);
  }

  private get taskGroups(): ProjectTemplateTaskGroup[] {
    return this.template.taskGroups;
  }

  private created() {
    this.initTemplate();
    this.initTaskTypes();
  }

  private async initTemplate() {
    const t = await templateApi.get(this.id);
    if (t) {
      this.template = { ...t };
    }
  }

  private async initTaskTypes() {
    this.taskTypes = await getTaskTypes();
  }

  private getTaskTypeName(id: number) {
    const t = this.taskTypes.find(t => t.taskTypeId === id);
    return t ? t.name : "";
  }

  private async createTask(task: ProjectTemplateTask) {
    await templateApi.createTask(task);
    await this.initTemplate();
  }

  private async updateTask(task: ProjectTemplateTask) {
    await templateApi.updateTask(task);
    await this.initTemplate();
  }

  private async updateTaskGroup(group: ProjectTemplateTaskGroup) {
    await templateApi.updateTaskGroup(group);
    await this.initTemplate();
  }

  private includesTask(t: ProjectTemplateTask): boolean {
    return areIntervalsOverlapping(t, this.chart);
  }

  private createTaskBar(t: ProjectTemplateTask): ChartBar {
    const b = ChartBarFactory.create(t, this.chart);
    return {
      ...b,
      id: t.id,
      label: t.name
    };
  }

  private goListPage() {
    this.$router.push({ name: "plan-list" });
  }
}
